// src/auth/use-force-logout.ts
import { useEffect } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { createHubConnection } from '@/shared/realtime/signalr-connection';
import { useAuthStore } from './auth-store';

const HUB_PATH = '/api/identity-server/NotificationsHub';

interface ForceLogoutFrame {
  userId?: string;
  appType?: string;
}

function unwrapFrame(args: unknown[]): ForceLogoutFrame | null {
  const first = args[0];
  if (first && typeof first === 'object') return first as ForceLogoutFrame;
  return null;
}

/** Listens for the Identity hub `UpdateUser` event; fires `onForceLogout` only
 *  when the event targets THIS Provider user (matches Flutter menu_container). */
export function useForceLogout(onForceLogout: () => void): void {
  const status = useAuthStore((s) => s.status);
  const userId = useAuthStore((s) => s.user?.userId);

  useEffect(() => {
    if (status !== 'authenticated' || !userId) return;

    const hub = createHubConnection(HUB_PATH, () =>
      useAuthStore.getState().getValidAccessToken(),
    );

    hub.on('UpdateUser', (...args: unknown[]) => {
      const msg = unwrapFrame(args);
      if (msg?.userId === userId && msg?.appType?.trim() === 'Provider') {
        void hub.stop().catch(() => {});
        onForceLogout();
      }
    });

    let disposed = false;
    void (async () => {
      try {
        await hub.start();
        if (disposed && hub.state !== HubConnectionState.Disconnected) await hub.stop();
      } catch {
        /* withAutomaticReconnect handles transient failures */
      }
    })();

    return () => {
      disposed = true;
      if (hub.state === HubConnectionState.Connected) void hub.stop().catch(() => {});
    };
  }, [status, userId, onForceLogout]);
}
